import React, { useCallback, useMemo } from 'react';
import type { Action, ActionFn, DataGridProps, SetIsLoading } from '#/common/components/DataGrid';
import DataGrid, { defaultActions } from '#/common/components/DataGrid';
import columns from './columns';
import { actions } from './actions';
import { gql } from '@apollo/react-hooks';
import { getUserList } from '#/graphql/queries';
import useUserDelete from '../../hooks/useDeleteUserMutation';
import useToast from '#/common/hooks/useToast';
import { User } from '#/graphql/types';
import useFetchCognitoUsers from '../../hooks/useFetchCognitoUsersMutation';
import { UserRole } from '#/common/types/userTypes';
import useSession from '#/common/hooks/useSession';

const userSelectedActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: [],
  [UserRole.trane_admin]: [],
  [UserRole.technician]: [],
};

const gridActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: [
    ...defaultActions.map((a) => (typeof a === 'string' ? a : a.key)),
    'sync-users',
  ],
  [UserRole.trane_admin]: [
    ...defaultActions.map((a) => (typeof a === 'string' ? a : a.key)),
    'sync-users',
  ],
  [UserRole.technician]: [
    ...defaultActions.map((a) => (typeof a === 'string' ? a : a.key)),
    'sync-users',
  ],
};

const gridOptionsPerRole: Record<UserRole, Partial<DataGridProps<User>>> = {
  [UserRole.asset_manager]: { selectionMode: 'none' },
  [UserRole.trane_admin]: { selectionMode: 'none' },
  [UserRole.technician]: { selectionMode: 'none' },
};

const getUserListQuery = gql(getUserList);

const UserDataGrid: React.FC = () => {
  const { confirm } = useToast();
  const { fetchCognitoUsers, result } = useFetchCognitoUsers();

  const { deleteUser } = useUserDelete();

  const { user } = useSession();
  const role = user?.role as UserRole | undefined;

  const deleteItem = useCallback(
    async (ids: string[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      if (!ids) {
        return undefined;
      }
      const sure = await confirm('error', `Are you sure you want to delete those users?`);
      if (!sure) {
        return;
      }
      setIsLoading && setIsLoading(true, 'Deleting data...');
      await deleteUser(ids.join(','));
      setIsLoading && setIsLoading(false);
    },
    [deleteUser],
  );

  const userSelectedActions: ActionFn<User>[] = useMemo(() => {
    const res: ActionFn<User>[] = [];

    if (role && userSelectedActionsPerRole[role]?.includes('delete-model')) {
      res.push({
        key: 'delete-users',
        icon: 'trash',
        text: 'Delete',
        onClick: async (e) => {
          await deleteItem(
            e.selectedKeys.map((i: any) => i.id || i),
            { setIsLoading: e.setIsLoading },
          );
          e.dg?.instance.clearSelection();
          e.dg?.instance.refresh();
        },
      });
    }

    return res;
  }, [role, deleteItem]);

  const gridActions = useMemo<Action<User>[]>(() => {
    return [
      ...actions,
      {
        key: 'sync-users',
        icon: 'cloud_sync',
        text: '+ Sync',
        showTextOnly: true,
        onClick: () => fetchUsers(),
      },
    ]
      .filter((a) => role && gridActionsPerRole[role]?.includes(typeof a === 'string' ? a : a.key))
      .map((a) => {
        if (typeof a === 'string') {
          return a;
        }
        const result = { ...a };
        return result;
      })
      .filter((a) => !!a);
  }, [role]);

  const fetchUsers = async () => {
    const r = await fetchCognitoUsers();
  };

  if (!role) return null;

  return (
    <>
      <DataGrid
        id="users"
        pluralText="Users"
        singularText="User"
        columns={columns}
        recordUrl="/admin/users/:id"
        actions={gridActions}
        selectedActions={userSelectedActions}
        getItemsQuery={getUserListQuery}
        {...gridOptionsPerRole[role]}
      />
    </>
  );
};

export default React.memo(UserDataGrid);
